import axios from 'axios';
import {API_BASE_URL} from './authApi';

export interface ProfileApiAthleticService {
  _id?: string;
  sportName: string;
  skillLevel: string;
  yearsOfExperience?: number;
  certifications?: string[];
  availability?: string;
}

export interface ProfileApiArtisticExperience {
  _id?: string;
  artForm: string;
  proficiencyLevel: string;
  portfolioLinks?: string[];
  yearsOfPractice?: number;
  serviceRates?: string;
}

export interface ProfileApiGeneralService {
  _id?: string;
  title: string;
  description: string;
  pricing?: string;
  availability?: string;
  contactPreferences?: string;
}

export interface ProfileApiCollegeService {
  _id?: string;
  opportunityType: string;
  title: string;
  description: string;
  eligibility?: string;
  deadline?: string;
  learnMoreUrl?: string;
}

export interface ProfileApiSocialLinks {
  facebook?: string;
  instagram?: string;
  twitter?: string;
  linkedin?: string;
  youtube?: string;
  tiktok?: string;
  website?: string;
  other?: Array<{
    platform: string;
    url: string;
  }>;
}

export type ProfileApiSocialProvider =
  | 'facebook'
  | 'instagram'
  | 'twitter'
  | 'linkedin'
  | 'youtube'
  | 'tiktok'
  | 'website';

export type ProfileApiSocialConnectionStatus =
  | 'connected'
  | 'disconnected'
  | 'needs_public_url'
  | 'failed';

export type ProfileApiSocialConnectionMethod =
  | 'oauth'
  | 'oauth_plus_manual_url'
  | 'manual_url'
  | 'legacy_import';

export interface ProfileApiFacebookPageOption {
  id: string;
  name: string;
  link?: string;
  avatarUrl?: string;
}

export interface ProfileApiSocialConnection {
  provider: ProfileApiSocialProvider;
  status: ProfileApiSocialConnectionStatus;
  connectionMethod?: ProfileApiSocialConnectionMethod;
  providerAccountId?: string;
  usernameOrHandle?: string;
  displayName?: string;
  avatarUrl?: string;
  profileUrl?: string;
  linkedAt?: string;
  lastVerifiedAt?: string;
  lastError?: string;
}

export type ProfileApiSocialConnections = Record<ProfileApiSocialProvider, ProfileApiSocialConnection>;

export type ProfileApiHomeHeaderTheme =
  | 'purple'
  | 'pink'
  | 'blue'
  | 'green'
  | 'orange'
  | 'dark'
  | 'rainbow'
  | 'gold'
  | 'custom';

export interface ProfileApiHomeHeaderAppearance {
  theme: ProfileApiHomeHeaderTheme;
  customImageUrl?: string;
  customImageObjectKey?: string;
  imagePosition: {
    x: number;
    y: number;
  };
  imageZoom: number;
  updatedAt?: string;
}

type SinglePutProfileAssetUploadPurpose = 'profile-picture' | 'home-header-image';
type SinglePutProfileAssetUploadStrategy = 'single-put';

interface SinglePutProfileAssetUploadSessionRecord<P extends SinglePutProfileAssetUploadPurpose> {
  id: string;
  purpose: P;
  strategy: SinglePutProfileAssetUploadStrategy;
  fileName: string;
  contentType: string;
  fileSize: number;
  objectKey: string;
  publicUrl: string;
  expiresAt: string;
}

interface SinglePutProfileAssetUploadSessionData<P extends SinglePutProfileAssetUploadPurpose> {
  uploadSession: SinglePutProfileAssetUploadSessionRecord<P>;
  upload: {
    strategy: SinglePutProfileAssetUploadStrategy;
    uploadUrl?: string;
    headers?: Record<string, string>;
  };
}

export type ProfilePictureUploadPurpose = 'profile-picture';
export type HomeHeaderImageUploadPurpose = 'home-header-image';
export type ProfilePictureUploadStrategy = SinglePutProfileAssetUploadStrategy;
export type HomeHeaderImageUploadStrategy = SinglePutProfileAssetUploadStrategy;
export type ProfilePictureUploadSessionRecord = SinglePutProfileAssetUploadSessionRecord<'profile-picture'>;
export type HomeHeaderImageUploadSessionRecord = SinglePutProfileAssetUploadSessionRecord<'home-header-image'>;
export type ProfilePictureUploadSessionData = SinglePutProfileAssetUploadSessionData<'profile-picture'>;
export type HomeHeaderImageUploadSessionData = SinglePutProfileAssetUploadSessionData<'home-header-image'>;

export interface ProfileApiUser {
  _id: string;
  email: string;
  accountType: 'individual' | 'enterprise';
  planTier?: 'free' | 'pro';
  isPro?: boolean;
  profile: {
    name: string;
    bio?: string;
    profilePicture?: string;
    profilePictureObjectKey?: string;
    location?: string;
  };
  socialLinks?: ProfileApiSocialLinks;
  socialConnections?: ProfileApiSocialConnections;
  athleticServices: ProfileApiAthleticService[];
  artisticExperiences: ProfileApiArtisticExperience[];
  generalServices: ProfileApiGeneralService[];
  collegeServices: ProfileApiCollegeService[];
  highlightReels?: Array<{_id?: string}>;
  mediaContent?: Array<{_id?: string}>;
  stats?: {
    followerCount?: number;
    followingCount?: number;
  };
  appearance?: {
    homeHeader?: ProfileApiHomeHeaderAppearance;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface ProfileApiUpdateInput {
  name: string;
}

interface ProfileApiResponse {
  success: boolean;
  profile: ProfileApiUser;
}

interface ProfileApiHomeHeaderAppearanceResponse {
  success: boolean;
  appearance: ProfileApiHomeHeaderAppearance;
}

interface CreateProfilePictureUploadSessionResponse {
  success: boolean;
  uploadSession: ProfilePictureUploadSessionRecord;
  upload: ProfilePictureUploadSessionData['upload'];
}

interface CreateHomeHeaderImageUploadSessionResponse {
  success: boolean;
  uploadSession: HomeHeaderImageUploadSessionRecord;
  upload: HomeHeaderImageUploadSessionData['upload'];
}

interface StartSocialConnectionResponse {
  success: boolean;
  provider: ProfileApiSocialProvider;
  authorizeUrl: string;
}

interface FacebookPageOptionsResponse {
  success: boolean;
  pages: ProfileApiFacebookPageOption[];
}

export interface ProfileApiAthleticServiceInput {
  sportName: string;
  skillLevel: string;
  yearsOfExperience?: number | null;
  certifications?: string[];
  availability?: string | null;
}

export interface ProfileApiAthleticServiceUpdate {
  sportName?: string;
  skillLevel?: string;
  yearsOfExperience?: number | null;
  certifications?: string[];
  availability?: string | null;
}

export interface ProfileApiArtisticExperienceInput {
  artForm: string;
  proficiencyLevel: string;
  portfolioLinks?: string[];
  yearsOfPractice?: number | null;
  serviceRates?: string | null;
}

export interface ProfileApiArtisticExperienceUpdate {
  artForm?: string;
  proficiencyLevel?: string;
  portfolioLinks?: string[];
  yearsOfPractice?: number | null;
  serviceRates?: string | null;
}

export interface ProfileApiGeneralServiceInput {
  title: string;
  description: string;
  pricing?: string | null;
  availability?: string | null;
  contactPreferences?: string | null;
}

export interface ProfileApiGeneralServiceUpdate {
  title?: string;
  description?: string;
  pricing?: string | null;
  availability?: string | null;
  contactPreferences?: string | null;
}

export interface ProfileApiCollegeServiceInput {
  opportunityType: string;
  title: string;
  description: string;
  eligibility?: string | null;
  deadline?: string | null;
  learnMoreUrl?: string | null;
}

export interface ProfileApiCollegeServiceUpdate {
  opportunityType?: string;
  title?: string;
  description?: string;
  eligibility?: string | null;
  deadline?: string | null;
  learnMoreUrl?: string | null;
}

export interface ProfileApiHomeHeaderCustomImagePayload {
  file?: File | null;
  imagePosition: {
    x: number;
    y: number;
  };
  imageZoom: number;
}

export interface ProfileApiProfilePictureUploadSessionPayload {
  fileName?: string;
  contentType: string;
  fileSize: number;
}

export interface ProfileApiHomeHeaderImageUploadSessionPayload {
  fileName?: string;
  contentType: string;
  fileSize: number;
}

export interface StartSocialConnectionPayload {
  returnPath?: string;
}

export interface ConfirmFacebookPageSelectionPayload {
  state: string;
  pageId: string;
}

type ProfileApiErrorResponse = {
  message?: string;
  error?: string | {message?: string};
};

export class ProfileApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ProfileApiError';
    this.status = status;
  }
}

const profileClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const DIRECT_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as ProfileApiErrorResponse | undefined;
    const nestedError =
      typeof errorData?.error === 'string'
        ? errorData.error
        : errorData?.error?.message;

    return errorData?.message || nestedError || error.message || fallbackMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
};

const requestProfile = async (
  method: 'get' | 'post' | 'put' | 'delete',
  url: string,
  token: string,
  data?: unknown,
  signal?: AbortSignal,
  headers?: Record<string, string>
): Promise<ProfileApiUser> => {
  try {
    const response = await profileClient.request<ProfileApiResponse>({
      method,
      url,
      data,
      signal,
      headers: {
        Authorization: `Bearer ${token}`,
        ...headers,
      },
    });

    return response.data.profile;
  } catch (error) {
    if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
      throw error;
    }

    throw new ProfileApiError(
      getErrorMessage(error, 'Failed to update profile'),
      axios.isAxiosError(error) ? error.response?.status : undefined
    );
  }
};

const requestHomeHeaderAppearance = async (
  method: 'get' | 'post' | 'put' | 'delete',
  url: string,
  token: string,
  data?: unknown,
  headers?: Record<string, string>,
  signal?: AbortSignal
): Promise<ProfileApiHomeHeaderAppearance> => {
  try {
    const response = await profileClient.request<ProfileApiHomeHeaderAppearanceResponse>({
      method,
      url,
      data,
      signal,
      headers: {
        Authorization: `Bearer ${token}`,
        ...headers,
      },
    });

    return response.data.appearance;
  } catch (error) {
    if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
      throw error;
    }

    throw new ProfileApiError(
      getErrorMessage(error, 'Failed to update home header appearance'),
      axios.isAxiosError(error) ? error.response?.status : undefined
    );
  }
};

const createSinglePutUploadSession = async <
  P extends SinglePutProfileAssetUploadPurpose,
  TResponse extends {
    uploadSession: SinglePutProfileAssetUploadSessionRecord<P>;
    upload: SinglePutProfileAssetUploadSessionData<P>['upload'];
  },
>(
  token: string,
  url: string,
  payload: {
    fileName?: string;
    contentType: string;
    fileSize: number;
  },
  fallbackMessage: string,
  signal?: AbortSignal
): Promise<SinglePutProfileAssetUploadSessionData<P>> => {
  try {
    const response = await profileClient.post<TResponse>(url, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal,
    });

    return {
      uploadSession: response.data.uploadSession,
      upload: response.data.upload,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
      throw error;
    }

    throw new ProfileApiError(
      getErrorMessage(error, fallbackMessage),
      axios.isAxiosError(error) ? error.response?.status : undefined
    );
  }
};

const uploadSinglePutFile = async <P extends SinglePutProfileAssetUploadPurpose>(
  sessionData: SinglePutProfileAssetUploadSessionData<P>,
  file: File,
  unsupportedMessage: string,
  options?: {
    signal?: AbortSignal;
    onProgress?: (fraction: number) => void;
  }
): Promise<void> => {
  if (sessionData.upload.strategy !== 'single-put' || !sessionData.upload.uploadUrl) {
    throw new ProfileApiError(unsupportedMessage);
  }

  await axios.put(sessionData.upload.uploadUrl, file, {
    timeout: DIRECT_UPLOAD_TIMEOUT_MS,
    headers: sessionData.upload.headers,
    signal: options?.signal,
    onUploadProgress: (event) => {
      if (!options?.onProgress) {
        return;
      }

      const total = Number.isFinite(file.size) && file.size > 0 ? file.size : 0;
      const loaded = Number(event.loaded || 0);
      options.onProgress(total > 0 ? Math.min(Math.max(loaded / total, 0), 1) : 0);
    },
  });
};

const abortSinglePutUploadSession = async (
  token: string,
  url: string,
  uploadSessionId: string,
  fallbackMessage: string,
  signal?: AbortSignal
): Promise<void> => {
  try {
    await profileClient.post(
      url,
      {uploadSessionId},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      }
    );
  } catch (error) {
    if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
      throw error;
    }

    throw new ProfileApiError(
      getErrorMessage(error, fallbackMessage),
      axios.isAxiosError(error) ? error.response?.status : undefined
    );
  }
};

export const profileApi = {
  async getMyProfile(
    token: string,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    try {
      const response = await profileClient.get<ProfileApiResponse>('/profiles/me', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      });

      return response.data.profile;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new ProfileApiError(
        getErrorMessage(error, 'Failed to load profile'),
        axios.isAxiosError(error) ? error.response?.status : undefined
      );
    }
  },

  async getProfile(
    userId: string,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    try {
      const response = await profileClient.get<ProfileApiResponse>(`/profiles/${encodeURIComponent(userId)}`, {
        signal,
      });

      return response.data.profile;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new ProfileApiError(
        getErrorMessage(error, 'Failed to load profile'),
        axios.isAxiosError(error) ? error.response?.status : undefined
      );
    }
  },

  async updateMyProfile(
    token: string,
    payload: ProfileApiUpdateInput,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('put', '/profiles/me', token, payload, signal);
  },

  async getMyHomeHeaderAppearance(
    token: string,
    signal?: AbortSignal
  ): Promise<ProfileApiHomeHeaderAppearance> {
    return requestHomeHeaderAppearance('get', '/profiles/me/appearance/home-header', token, undefined, undefined, signal);
  },

  async updateMyHomeHeaderTheme(
    token: string,
    theme: Exclude<ProfileApiHomeHeaderTheme, 'custom'>,
    signal?: AbortSignal
  ): Promise<ProfileApiHomeHeaderAppearance> {
    return requestHomeHeaderAppearance(
      'put',
      '/profiles/me/appearance/home-header/theme',
      token,
      {theme},
      undefined,
      signal
    );
  },

  async updateMyHomeHeaderCustomImage(
    token: string,
    payload: ProfileApiHomeHeaderCustomImagePayload,
    signal?: AbortSignal
  ): Promise<ProfileApiHomeHeaderAppearance> {
    if (payload.file) {
      const uploadSessionData = await this.createMyHomeHeaderImageUploadSession(token, {
        fileName: payload.file.name,
        contentType: payload.file.type,
        fileSize: payload.file.size,
      }, signal);

      try {
        await this.uploadMyHomeHeaderImageFile(uploadSessionData, payload.file, {signal});
        return await this.completeMyHomeHeaderImageUpload(token, uploadSessionData.uploadSession.id, {
          imagePosition: payload.imagePosition,
          imageZoom: payload.imageZoom,
        }, signal);
      } catch (error) {
        try {
          await this.abortMyHomeHeaderImageUpload(token, uploadSessionData.uploadSession.id);
        } catch (abortError) {
          if (!(abortError instanceof Error) || abortError.name !== 'CanceledError') {
            console.error('Failed to abort home header image upload session:', abortError);
          }
        }

        throw error;
      }
    }

    const formData = new FormData();

    formData.append('imagePositionX', String(payload.imagePosition.x));
    formData.append('imagePositionY', String(payload.imagePosition.y));
    formData.append('imageZoom', String(payload.imageZoom));

    return requestHomeHeaderAppearance(
      'put',
      '/profiles/me/appearance/home-header/custom-image',
      token,
      formData,
      {'Content-Type': 'multipart/form-data'},
      signal
    );
  },

  async createMyHomeHeaderImageUploadSession(
    token: string,
    payload: ProfileApiHomeHeaderImageUploadSessionPayload,
    signal?: AbortSignal
  ): Promise<HomeHeaderImageUploadSessionData> {
    return createSinglePutUploadSession<'home-header-image', CreateHomeHeaderImageUploadSessionResponse>(
      token,
      '/profiles/me/appearance/home-header/upload-session',
      payload,
      'Failed to prepare home header image upload',
      signal
    );
  },

  async uploadMyHomeHeaderImageFile(
    sessionData: HomeHeaderImageUploadSessionData,
    file: File,
    options?: {
      signal?: AbortSignal;
      onProgress?: (fraction: number) => void;
    }
  ): Promise<void> {
    await uploadSinglePutFile(
      sessionData,
      file,
      'This home header image upload session does not support direct uploads.',
      options
    );
  },

  async completeMyHomeHeaderImageUpload(
    token: string,
    uploadSessionId: string,
    payload: Omit<ProfileApiHomeHeaderCustomImagePayload, 'file'>,
    signal?: AbortSignal
  ): Promise<ProfileApiHomeHeaderAppearance> {
    return requestHomeHeaderAppearance(
      'post',
      '/profiles/me/appearance/home-header/complete',
      token,
      {
        uploadSessionId,
        imagePositionX: payload.imagePosition.x,
        imagePositionY: payload.imagePosition.y,
        imageZoom: payload.imageZoom,
      },
      undefined,
      signal
    );
  },

  async abortMyHomeHeaderImageUpload(
    token: string,
    uploadSessionId: string,
    signal?: AbortSignal
  ): Promise<void> {
    return abortSinglePutUploadSession(
      token,
      '/profiles/me/appearance/home-header/abort',
      uploadSessionId,
      'Failed to abort home header image upload',
      signal
    );
  },

  async removeMyHomeHeaderCustomImage(
    token: string,
    signal?: AbortSignal
  ): Promise<ProfileApiHomeHeaderAppearance> {
    return requestHomeHeaderAppearance(
      'delete',
      '/profiles/me/appearance/home-header/custom-image',
      token,
      undefined,
      undefined,
      signal
    );
  },

  async updateMyProfilePicture(
    token: string,
    file: File,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    const uploadSessionData = await this.createMyProfilePictureUploadSession(token, {
      fileName: file.name,
      contentType: file.type,
      fileSize: file.size,
    }, signal);

    try {
      await this.uploadMyProfilePictureFile(uploadSessionData, file, {signal});
      return await this.completeMyProfilePictureUpload(token, uploadSessionData.uploadSession.id, signal);
    } catch (error) {
      try {
        await this.abortMyProfilePictureUpload(token, uploadSessionData.uploadSession.id);
      } catch (abortError) {
        if (!(abortError instanceof Error) || abortError.name !== 'CanceledError') {
          console.error('Failed to abort profile picture upload session:', abortError);
        }
      }

      throw error;
    }
  },

  async removeMyProfilePicture(
    token: string,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile(
      'delete',
      '/profiles/me/profile-picture',
      token,
      undefined,
      signal
    );
  },

  async createMyProfilePictureUploadSession(
    token: string,
    payload: ProfileApiProfilePictureUploadSessionPayload,
    signal?: AbortSignal
  ): Promise<ProfilePictureUploadSessionData> {
    try {
      const response = await profileClient.post<CreateProfilePictureUploadSessionResponse>(
        '/profiles/me/profile-picture/upload-session',
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );

      return {
        uploadSession: response.data.uploadSession,
        upload: response.data.upload,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new ProfileApiError(
        getErrorMessage(error, 'Failed to prepare profile picture upload'),
        axios.isAxiosError(error) ? error.response?.status : undefined
      );
    }
  },

  async uploadMyProfilePictureFile(
    sessionData: ProfilePictureUploadSessionData,
    file: File,
    options?: {
      signal?: AbortSignal;
      onProgress?: (fraction: number) => void;
    }
  ): Promise<void> {
    if (sessionData.upload.strategy !== 'single-put' || !sessionData.upload.uploadUrl) {
      throw new ProfileApiError('This profile picture upload session does not support direct uploads.');
    }

    await axios.put(sessionData.upload.uploadUrl, file, {
      timeout: DIRECT_UPLOAD_TIMEOUT_MS,
      headers: sessionData.upload.headers,
      signal: options?.signal,
      onUploadProgress: (event) => {
        if (!options?.onProgress) {
          return;
        }

        const total = Number.isFinite(file.size) && file.size > 0 ? file.size : 0;
        const loaded = Number(event.loaded || 0);
        options.onProgress(total > 0 ? Math.min(Math.max(loaded / total, 0), 1) : 0);
      },
    });
  },

  async completeMyProfilePictureUpload(
    token: string,
    uploadSessionId: string,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile(
      'post',
      '/profiles/me/profile-picture/complete',
      token,
      {uploadSessionId},
      signal
    );
  },

  async abortMyProfilePictureUpload(
    token: string,
    uploadSessionId: string,
    signal?: AbortSignal
  ): Promise<void> {
    try {
      await profileClient.post(
        '/profiles/me/profile-picture/abort',
        {uploadSessionId},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new ProfileApiError(
        getErrorMessage(error, 'Failed to abort profile picture upload'),
        axios.isAxiosError(error) ? error.response?.status : undefined
      );
    }
  },

  async addAthleticService(
    token: string,
    payload: ProfileApiAthleticServiceInput,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('post', '/profiles/me/athletic', token, payload, signal);
  },

  async updateAthleticService(
    token: string,
    id: string,
    payload: ProfileApiAthleticServiceUpdate,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('put', `/profiles/me/athletic/${id}`, token, payload, signal);
  },

  async deleteAthleticService(
    token: string,
    id: string,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('delete', `/profiles/me/athletic/${id}`, token, undefined, signal);
  },

  async addArtisticExperience(
    token: string,
    payload: ProfileApiArtisticExperienceInput,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('post', '/profiles/me/artistic', token, payload, signal);
  },

  async updateArtisticExperience(
    token: string,
    id: string,
    payload: ProfileApiArtisticExperienceUpdate,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('put', `/profiles/me/artistic/${id}`, token, payload, signal);
  },

  async deleteArtisticExperience(
    token: string,
    id: string,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('delete', `/profiles/me/artistic/${id}`, token, undefined, signal);
  },

  async addGeneralService(
    token: string,
    payload: ProfileApiGeneralServiceInput,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('post', '/profiles/me/services', token, payload, signal);
  },

  async updateGeneralService(
    token: string,
    id: string,
    payload: ProfileApiGeneralServiceUpdate,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('put', `/profiles/me/services/${id}`, token, payload, signal);
  },

  async deleteGeneralService(
    token: string,
    id: string,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('delete', `/profiles/me/services/${id}`, token, undefined, signal);
  },

  async addCollegeService(
    token: string,
    payload: ProfileApiCollegeServiceInput,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('post', '/profiles/me/college-services', token, payload, signal);
  },

  async updateCollegeService(
    token: string,
    id: string,
    payload: ProfileApiCollegeServiceUpdate,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('put', `/profiles/me/college-services/${id}`, token, payload, signal);
  },

  async deleteCollegeService(
    token: string,
    id: string,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('delete', `/profiles/me/college-services/${id}`, token, undefined, signal);
  },

  async startSocialConnection(
    token: string,
    provider: ProfileApiSocialProvider,
    payload?: StartSocialConnectionPayload,
    signal?: AbortSignal
  ): Promise<string> {
    try {
      const response = await profileClient.post<StartSocialConnectionResponse>(
        `/social-connections/${provider}/start`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );

      return response.data.authorizeUrl;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new ProfileApiError(
        getErrorMessage(error, 'Failed to start social account linking'),
        axios.isAxiosError(error) ? error.response?.status : undefined
      );
    }
  },

  async saveWebsiteSocialConnection(
    token: string,
    profileUrl: string,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('put', '/social-connections/website', token, {profileUrl}, signal);
  },

  async getFacebookPageOptions(
    token: string,
    state: string,
    signal?: AbortSignal
  ): Promise<ProfileApiFacebookPageOption[]> {
    try {
      const response = await profileClient.get<FacebookPageOptionsResponse>(
        `/social-connections/facebook/page-options?state=${encodeURIComponent(state)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );

      return Array.isArray(response.data.pages) ? response.data.pages : [];
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new ProfileApiError(
        getErrorMessage(error, 'Failed to load Facebook Page options'),
        axios.isAxiosError(error) ? error.response?.status : undefined
      );
    }
  },

  async confirmFacebookPageSelection(
    token: string,
    payload: ConfirmFacebookPageSelectionPayload,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('post', '/social-connections/facebook/page-selection', token, payload, signal);
  },

  async confirmSocialConnectionPublicUrl(
    token: string,
    provider: Exclude<ProfileApiSocialProvider, 'website'>,
    profileUrl: string,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('put', `/social-connections/${provider}/public-url`, token, {profileUrl}, signal);
  },

  async disconnectSocialConnection(
    token: string,
    provider: ProfileApiSocialProvider,
    signal?: AbortSignal
  ): Promise<ProfileApiUser> {
    return requestProfile('delete', `/social-connections/${provider}`, token, undefined, signal);
  },
};
